import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { View, Element } from '../../core/types';
import i18n from '../../i18n';

export interface CalloutElement extends Element {
    category: 'Views';
    geometryData: {
        rect: { x: number; y: number; width: number; height: number; z: number };
        viewId: string;
    };
}

/**
 * Callout tool (View tab > Create > Callout).
 *
 * Two clicks define a plan rectangle. Creates a 'Callout' view carrying the
 * crop region, plus a Callout Marker symbol in the source plan — and switches
 * straight to the new view, like BIM.
 */
export class CalloutTool extends BaseTool {
    id = 'VIEW_CALLOUT';
    name = 'Callout';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
            this.updatePreview();
        } else {
            this.createCallout(this.startPoint, snapped);
            this.startPoint = null;
            this.currentPoint = null;
            this.context.appStore.setToolPreview(null);
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    private updatePreview() {
        if (!this.context || !this.startPoint || !this.currentPoint) return;
        this.context.appStore.setToolPreview({
            type: 'line',
            start: this.startPoint,
            end: this.currentPoint,
            color: '#00aaff',
        });
    }

    private createCallout(corner1: THREE.Vector3, corner2: THREE.Vector3) {
        if (!this.context) return;

        const viewId = uuidv4();
        const elementId = uuidv4();
        const name = `Callout ${this.context.bimStore.project.views.size + 1}`;

        const x = Math.min(corner1.x, corner2.x);
        const y = Math.min(corner1.y, corner2.y);
        const width = Math.abs(corner2.x - corner1.x);
        const height = Math.abs(corner2.y - corner1.y);
        if (width < 0.01 || height < 0.01) return;

        const rect = { x, y, width, height, z: corner1.z };
        // Crop bounds may need reordering (min/max); keep them canonical
        const cropRegion = { x: Math.min(x, x + width), y: Math.min(y, y + height), width, height };

        const sourceView = this.context.bimStore.project.views.get(
            this.context.bimStore.project.activeViewId
        );

        const newView: View = {
            id: viewId,
            name,
            type: 'Callout',
            discipline: 'Architectural',
            scale: 50,
            detailLevel: 'Fine',
            visibility: {},
            filters: [],
            cropRegion,
            associatedLevelId: sourceView?.associatedLevelId,
            viewRange: sourceView?.viewRange,
            camera: sourceView
                ? {
                    // Inherit the source plan's top-down pose
                    position: [cropRegion.x + width / 2, cropRegion.y + height / 2, 50],
                    target: [cropRegion.x + width / 2, cropRegion.y + height / 2, 0],
                    projection: 'Orthographic',
                }
                : undefined,
        };

        const marker: CalloutElement = {
            id: elementId,
            category: 'Views',
            type: 'Callout Marker',
            parameters: {
                'View Name': name,
                'View Id': viewId,
            },
            constraints: [],
            geometryData: { rect, viewId },
        };

        this.context.bimStore.addView(newView);
        this.context.bimStore.addElement(marker);
        // BIM switches to the callout immediately after it is drawn
        this.context.bimStore.setActiveView(viewId);
        // Crop the callout view to its own region like BIM does
        this.context.appStore.updateView({ cropView: true, showCropRegion: true });
        this.notify(i18n.t('tools.notify.calloutCreated', { name: name }), 'success');
    }
}
